import AiCitySuggestion from "../../common/AiCitySuggestion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Divider,
  Typography,
  Box,
  Icon,
  InputAdornment,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import PropTypes from "prop-types";
import { CountriesV2Repository } from "../../repositories/CountriesV2Repository";
import { CarrierRepository } from "../../repositories/CarrierRepository";
import { ErrorMessageResolver } from "../../common/ErrorMessageResolver";
import { useDispatch } from "react-redux";
import { notifyShowErrorMessage } from "../../common/CommonActions";
import { LOCALE } from "../../properties/Locale";
import { OrganizationsRepository } from "../../repositories/OrganizationsRepository";

function CreateCarrierFromConsignmentDialog({
  open,
  onClose,
  onSave,
  organization,
  initialValue = null,
  lockType = false,
}) {
  const isEdit = !!initialValue?.id;

  const emptyForm = {
    name: "",
    eori: "",
    address: "",
    addressNumber: "",
    postCode: "",
    city: null,
    country: null,
    contactEmail: "",
    contactPhone: "",
    organization: organization,
  };

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [clientOrganizations, setClientOrganizations] = useState([]);

  const dispatch = useDispatch();
  const firstFieldRef = useRef(null);

  // stability guards
  const focusTimerRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const mountedRef = useRef(false);

  // country-change logic guards
  const prevCountryCodeRef = useRef(null);
  const hydratingRef = useRef(false);

  const optionIncludes = (opts, sel, key = "id") =>
    !!(sel && opts?.some((o) => String(o?.[key]) === String(sel?.[key])));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
    };
  }, []);

  // Reset & prefill when dialog opens
  useEffect(() => {
    if (!open) return;

    setErrors({});
    setTouched({});
    setSaving(false);
    setCities([]);
    hasFocusedRef.current = false;

    // Focus once after open
    focusTimerRef.current = setTimeout(() => {
      if (!hasFocusedRef.current) {
        firstFieldRef.current?.focus();
        hasFocusedRef.current = true;
      }
    }, 0);

    (async () => {
      await getAllCountries();

      // Prefill from organization if no initialValue
      if (organization && !initialValue) {
        setForm({
          ...emptyForm,
          organization,
          name: organization.nameEn || "",
        });
      } else {
        await hydrateFromInitialOrEmpty();
      }
    })();

    return () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep organization in sync, avoid redundant set
  useEffect(() => {
    if (!organization) return;
    setForm((prev) => {
      const same =
        (prev.organization?.id ?? null) === (organization?.id ?? null);
      return same ? prev : { ...prev, organization };
    });
  }, [organization]);

  // Country change effect — preserves city during hydrate, only clears on real user change
  useEffect(() => {
    const code = form.country?.code ?? null;

    // No country selected now
    if (code === null) {
      if (
        !hydratingRef.current &&
        prevCountryCodeRef.current !== null &&
        form.city
      ) {
        setForm((prev) => ({ ...prev, city: null }));
      }
      setCities((prev) => (prev.length ? [] : prev));
      prevCountryCodeRef.current = null;
      return;
    }

    // Country changed (including initial set)
    if (prevCountryCodeRef.current !== code) {
      const wasNonNull = prevCountryCodeRef.current !== null;
      prevCountryCodeRef.current = code;

      // Clear city only if this is a real user change (i.e., we had a previous non-null country)
      if (!hydratingRef.current && wasNonNull && form.city) {
        setForm((prev) => ({ ...prev, city: null }));
      }

      getAllCitiesByCountry(code);
    }

    // If country code is the same, do nothing (keeps prefilled city)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.country?.code]);

  const hydrateFromInitialOrEmpty = async () => {
    hydratingRef.current = true;
    try {
      if (initialValue) {
        const { missingFields, aiCitySuggestion, ...prefill } = initialValue;
        const next = {
          ...emptyForm,
          ...prefill,
          organization: initialValue?.organization ?? organization,
        };

        for (const field of ["name", "eori", "address", "addressNumber", "postCode", "contactEmail", "contactPhone"]) next[field] = next[field] ?? "";

        // If we have a country, load its cities so the city value is resolvable
        if (next.country?.code) {
          await getAllCitiesByCountry(next.country.code);
          prevCountryCodeRef.current = next.country.code; // align the prev ref to avoid clearing on next render
        } else {
          setCities((prev) => (prev.length ? [] : prev));
          next.city = null;
          prevCountryCodeRef.current = null;
        }

        // ensure selected options exist in lists
        setCountries((prev) =>
          !next.country || optionIncludes(prev, next.country)
            ? prev
            : [next.country, ...(prev || [])]
        );
        setCities((prev) =>
          !next.city || optionIncludes(prev, next.city) ? prev : [next.city, ...(prev || [])]
        );

        setForm(next);
      } else {
        prevCountryCodeRef.current = null;
        setForm((prev) => ({
          ...emptyForm,
          organization: organization,
        }));
      }
    } finally {
      hydratingRef.current = false;
    }
  };

  const cityOptions = useMemo(() => cities || [], [cities]);
  const countryOptions = useMemo(() => countries || [], [countries]);

  const setFieldTouched = (key) =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const handleChange = (key, value) => {
    const v = typeof value === "string" ? value.replace(/\s{2,}/g, " ") : value;

    setForm((prev) => {
      // Special logic when organization changes
      if (key === "organization" && value) {
        const nextForm = {
          ...prev,
          organization: value,
          name: value.nameEn || "",
        };
        // Re-run validation so errors clear if the fields are now filled
        validate(nextForm);
        return nextForm;
      }

      // If organization cleared
      if (key === "organization" && !value) {
        const nextForm = { ...prev, organization: null };
        validate(nextForm);
        return nextForm;
      }

      // Default: single field update
      const nextForm = { ...prev, [key]: v };
      validate(nextForm);
      return nextForm;
    });
  };

  const phoneRegex = /^\+(?:[0-9] ?){6,14}[0-9]$/;
  const NAME_RX = /^[a-zA-Z0-9\s\/\-\,\.\']+$/;
  const NAME_RX_MSG =
    "can only contain Latin letters, numbers, spaces, and / - , . '";

  const validate = (draft = form) => {
    const e = {};
    if (!draft.name?.trim()) {
      e.name = "Name is required";
    } else if (!NAME_RX.test(draft.name.trim())) {
      e.name = `Name ${NAME_RX_MSG}`;
    }

    if (!draft.eori?.trim()) e.eori = "EORI is required";
    if (!draft.country) e.country = "Country is required";
    if (!draft.city) e.city = "City is required";

    if (!draft.contactEmail?.trim()) {
      e.contactEmail = "Contact email is required";
    } else {
      const email = draft.contactEmail.trim();
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!emailOk) e.contactEmail = "Invalid email address";
    }

    if (draft.contactPhone?.trim()) {
      const phone = draft.contactPhone.trim();
      if (!phoneRegex.test(phone)) {
        e.contactPhone =
          "Phone must start with + and contain 7–15 digits (spaces allowed)";
      }
    }

    if (!draft.address?.trim()) {
      e.address = "Address is required";
    } else if (!NAME_RX.test(draft.address.trim())) {
      e.address = `Address ${NAME_RX_MSG}`;
    }

    if (!draft.addressNumber?.trim()) {
      e.addressNumber = "Address number is required";
    } else if (!NAME_RX.test(draft.addressNumber.trim())) {
      e.addressNumber = `Address number ${NAME_RX_MSG}`;
    }

    if (!draft.postCode?.trim()) e.postCode = "Post code is required";

    setErrors(e);
    return e;
  };

  const focusFirstError = () => {
    const order = [
      "name",
      "eori",
      "country",
      "city",
      "address",
      "addressNumber",
      "postCode",
      "contactEmail",
      "contactPhone",
    ];
    const firstKey = order.find((k) => errors[k]);
    if (!firstKey) return;
    const el = document.querySelector(
      `[name="carrier-${firstKey}"] input, [name="carrier-${firstKey}"]`
    );
    if (el) el.focus();
  };

  const handleSave = async () => {
    const currentErrors = validate();
    setTouched({
      name: true,
      eori: true,
      country: true,
      city: true,
      contactEmail: touched.contactEmail || !!form.contactEmail,
      address: true,
      addressNumber: true,
      postCode: true,
      contactPhone: touched.contactPhone || !!form.contactPhone,
    });
    if (Object.keys(currentErrors).length > 0) {
      focusFirstError();
      return;
    }

    try {
      if (mountedRef.current) setSaving(true);
      const payload = {
        ...form,
        name: form.name.trim(),
        eori: form.eori.trim(),
        address: form.address?.trim() || "",
        addressNumber: form.addressNumber?.trim() || "",
        postCode: form.postCode?.trim() || "",
        contactEmail: form.contactEmail?.trim() || "",
        contactPhone: form.contactPhone?.trim() || "",
      };

      let res;
      if (isEdit) res = await CarrierRepository.update(payload);
      else res = await CarrierRepository.create(payload);

      if (mountedRef.current) onSave(res.data);
    } catch (err) {
      if (mountedRef.current) {
        console.error(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const getAllCountries = async () => {
    if (!mountedRef.current) return;
    setLoadingCountries(true);
    try {
      const res = await CountriesV2Repository.fetchAllWithoutPaging();
      if (mountedRef.current) setCountries(res?.data || []);
    } catch (err) {
      if (mountedRef.current) console.error(err);
    } finally {
      if (mountedRef.current) setLoadingCountries(false);
    }
  };

  const getAllCitiesByCountry = async (code) => {
    if (!mountedRef.current) return;
    setLoadingCities(true);
    try {
      const res = await CountriesV2Repository.getCitiesByCountry(code);
      if (mountedRef.current) setCities(res?.data || []);
    } catch (err) {
      if (mountedRef.current) console.error(err);
    } finally {
      if (mountedRef.current) setLoadingCities(false);
    }
  };

  const Required = () => (
    <Typography component="span" color="error" aria-label="required">
      *
    </Typography>
  );

  // Keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Icon>local_shipping</Icon>
          <Box>
            <Typography variant="h6">
              {isEdit
                ? "Edit Carrier / Declarant"
                : "Create Carrier / Declarant"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit ? (
                <>
                  Update carrier / declarant details, required fields are marked
                  with <Required />
                </>
              ) : (
                <>
                  Add a new carrier / declarant, required fields are marked with
                  <Required />
                </>
              )}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {initialValue?.missingFields?.length > 0 && <Alert severity="info" sx={{ mb: 2 }}>
          Complete or correct the carrier/declarant details before saving. Fields to review: {initialValue.missingFields.map((field) => ({ name: "Name", eori: "EORI", country: "Country", city: "City", contactEmail: "Contact email", address: "Street", addressNumber: "Building number", postCode: "Postcode", contactPhone: "Contact phone" }[field] || field)).join(", ")}.
        </Alert>}
        <form autoComplete="off">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={7}>
              <Autocomplete
                noOptionsText={LOCALE.noOptionsText}
                autoComplete={false}
                size="small"
                value={form?.organization ? `${form.organization.nameEn}` : ""}
                disabled
                onChange={(event, newValue) => {
                  const selectedOrg = clientOrganizations?.find(
                    (organization) => `${organization.nameEn}` === newValue
                  );
                  handleChange("organization", selectedOrg || null);
                }}
                inputValue={organizationSearch || ""}
                onInputChange={(event, newInputValue) => {
                  setOrganizationSearch(newInputValue);
                }}
                options={clientOrganizations?.map(
                  (organization) => `${organization.nameEn}`
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={LOCALE.organization}
                    fullWidth
                    variant="outlined"
                    error={!!errors.organization}
                    helperText={errors.organization}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete disabled={lockType || saving}
                value={form?.type ?? null}
                autoComplete={false}
                options={["CARRIER", "DECLARANT", "CARRIER_AND_DECLARANT"]}
                getOptionLabel={(option) =>
                  option === "CARRIER"
                    ? "Carrier"
                    : option === "DECLARANT"
                    ? "Declarant"
                    : option === "CARRIER_AND_DECLARANT"
                    ? "Carrier and Declarant"
                    : ""
                }
                onChange={(e, newValue) => handleChange("type", newValue)}
                title="Type"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Type"
                    fullWidth
                    variant="outlined"
                    // error={!!errors.type}
                    helperText="If no type is selected, it will default to Carrier and Declarant."
                    FormHelperTextProps={{
                      style: { color: "#6c757d" }, // muted info color
                    }}
                    inputRef={firstFieldRef}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="carrier-name"
                label={
                  <>
                    Name <Required />
                  </>
                }
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={() => {
                  setFieldTouched("name");
                  validate();
                }}
                fullWidth
                size="small"
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>badge</Icon>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: "new-password" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="carrier-eori"
                label={
                  <>
                    EORI <Required />
                  </>
                }
                value={form.eori}
                onChange={(e) =>
                  handleChange("eori", e.target.value.toUpperCase())
                }
                onBlur={() => {
                  setFieldTouched("eori");
                  validate();
                }}
                fullWidth
                size="small"
                error={touched.eori && !!errors.eori}
                helperText={touched.eori && errors.eori}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>verified_user</Icon>
                    </InputAdornment>
                  ),
                  inputMode: "text",
                }}
                inputProps={{ autoComplete: "new-password", maxLength: 25 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <TextField
                name="carrier-address"
                label={
                  <>
                    Address <Required />
                  </>
                }
                value={form.address}
                onChange={(e) => handleChange("address", e.target.value)}
                onBlur={() => {
                  setFieldTouched("address");
                  validate();
                }}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>home</Icon>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: "new-password" }}
                error={touched.address && !!errors.address}
                helperText={touched.address && errors.address}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="carrier-addressNumber"
                label={
                  <>
                    Address Number <Required />
                  </>
                }
                value={form.addressNumber}
                onChange={(e) => handleChange("addressNumber", e.target.value)}
                onBlur={() => {
                  setFieldTouched("addressNumber");
                  validate();
                }}
                fullWidth
                size="small"
                inputProps={{ autoComplete: "new-password" }}
                error={touched.addressNumber && !!errors.addressNumber}
                helperText={touched.addressNumber && errors.addressNumber}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                options={countryOptions}
                loading={loadingCountries}
                value={form.country}
                onChange={(_, v) => {
                  setFieldTouched("country");
                  handleChange("country", v);
                }}
                getOptionLabel={(o) => (o && (o.name || o.isoCode || "")) || ""}
                isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="carrier-country"
                    label={
                      <>
                        Country <Required />
                      </>
                    }
                    size="small"
                    error={touched.country && !!errors.country}
                    helperText={touched.country && errors.country}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Icon>public</Icon>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingCountries ? (
                            <CircularProgress size={18} sx={{ mr: 1 }} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                options={cityOptions}
                loading={loadingCities}
                value={form.city}
                onChange={(_, v) => {
                  setFieldTouched("city");
                  handleChange("city", v);
                }}
                getOptionLabel={(o) =>
                  (o && (o.cityName || o.postCode || "")) || ""
                }
                isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                size="small"
                disabled={!form.country}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="carrier-city"
                    label={
                      <>
                        City <Required />
                      </>
                    }
                    error={touched.city && !!errors.city}
                    helperText={<> {
                      touched.city && errors.city
                        ? errors.city
                        : !form.country
                        ? "Select a country first"
                        : ""
                    } <AiCitySuggestion suggestion={initialValue?.aiCitySuggestion} city={form.city} countryCode={form.country?.code} /> </>}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Icon>location_city</Icon>
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingCities ? (
                            <CircularProgress size={18} sx={{ mr: 1 }} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="carrier-postCode"
                label={
                  <>
                    Post Code <Required />
                  </>
                }
                value={form.postCode}
                onChange={(e) => handleChange("postCode", e.target.value)}
                onBlur={() => {
                  setFieldTouched("postCode");
                  validate();
                }}
                fullWidth
                size="small"
                inputProps={{ autoComplete: "new-password", maxLength: 12 }}
                error={touched.postCode && !!errors.postCode}
                helperText={touched.postCode && errors.postCode}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                name="carrier-contactEmail"
                label={
                  <>
                    Contact Email <Required />
                  </>
                }
                value={form.contactEmail}
                onChange={(e) => handleChange("contactEmail", e.target.value)}
                onBlur={() => {
                  setFieldTouched("contactEmail");
                  validate();
                }}
                fullWidth
                error={touched.contactEmail && !!errors.contactEmail}
                helperText={touched.contactEmail && errors.contactEmail}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>mail</Icon>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: "new-password" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="carrier-contactPhone"
                label="Contact Phone"
                value={form.contactPhone}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
                onBlur={() => {
                  setFieldTouched("contactPhone");
                  validate();
                }}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>call</Icon>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: "new-password" }}
                error={touched.contactPhone && !!errors.contactPhone}
                helperText={touched.contactPhone && errors.contactPhone}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="text"
          startIcon={<Icon>close</Icon>}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={
            saving ? <CircularProgress size={18} /> : <Icon>save</Icon>
          }
          disabled={
            saving ||
            !form.name?.trim() ||
            !form.eori?.trim() ||
            !form.country ||
            !form.city
          }
        >
          {saving
            ? isEdit
              ? "Updating..."
              : "Saving..."
            : isEdit
            ? "Update"
            : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CreateCarrierFromConsignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  organization: PropTypes.object,
  initialValue: PropTypes.object,
  lockType: PropTypes.bool,
};

export default CreateCarrierFromConsignmentDialog;
